import React, { useEffect, useState, useRef } from 'react';
import { Link, RefreshCw, Copy, Unlink, Video, Cloud, Calendar, HardDrive, Folder, Zap, Mail, MessageSquare, Smartphone, Monitor } from 'lucide-react';
import { integrationsApi, channelsApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import EmailTemplateEditor from '../components/Einstellungen/EmailTemplateEditor';
import WebhooksSettings from '../components/Einstellungen/WebhooksSettings';
import './Pages.css';

export default function Integrations() {
  const { showToast } = useToast();
  const oauthIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const oauthWindowRef = useRef<Window | null>(null);

  useEffect(() => {
    return () => {
      if (oauthIntervalRef.current) clearInterval(oauthIntervalRef.current);
      oauthWindowRef.current?.close();
    };
  }, []);

  const [activeTab, setActiveTab] = useState<'email' | 'kalender' | 'video' | 'cloud' | 'messaging' | 'webhooks'>('email');
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [icalUrl, setIcalUrl] = useState('');
  // Zoom state
  const [zoomStatus, setZoomStatus] = useState<{ connected: boolean; account_email?: string; auto_create_meetings?: boolean; integration_id?: string }>({ connected: false });
  const [zoomAccountId, setZoomAccountId] = useState('');
  const [zoomClientId, setZoomClientId] = useState('');
  const [zoomClientSecret, setZoomClientSecret] = useState('');
  const [zoomConnecting, setZoomConnecting] = useState(false);
  // Google state
  const [googleStatus, setGoogleStatus] = useState<{
    connected: boolean; account_email?: string; auto_sync?: boolean; invite_clients?: boolean;
    auto_upload_invoices?: boolean; auto_upload_offers?: boolean; auto_upload_contracts?: boolean;
    auto_upload_documents?: boolean; auto_upload_session_notes?: boolean;
    drive_target_folder_id?: string; drive_target_folder_name?: string;
    drive_folders?: Record<string, string>;
    last_sync_at?: string; integration_id?: string;
  }>({ connected: false });
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [driveFolders, setDriveFolders] = useState<{ id: string; name: string }[]>([]);
  // Outlook state
  const [outlookStatus, setOutlookStatus] = useState<{ connected: boolean; account_email?: string; last_sync_at?: string; integration_id?: string }>({ connected: false });
  const [outlookClientId, setOutlookClientId] = useState('');
  const [outlookClientSecret, setOutlookClientSecret] = useState('');
  const [outlookConnecting, setOutlookConnecting] = useState(false);
  // Messaging channels
  const [waAccountSid, setWaAccountSid] = useState('');
  const [waAuthToken, setWaAuthToken] = useState('');
  const [waPhoneNumber, setWaPhoneNumber] = useState('');
  const [waTestPhone, setWaTestPhone] = useState('');
  const [waActive, setWaActive] = useState(false);
  const [teamsAppId, setTeamsAppId] = useState('');
  const [teamsAppSecret, setTeamsAppSecret] = useState('');
  const [teamsActive, setTeamsActive] = useState(false);
  const [channelsSaving, setChannelsSaving] = useState(false);

  useEffect(() => { loadIntegrations(); loadChannels(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChannels = async () => {
    try {
      const res = await channelsApi.configs();
      const configs = res.data || [];
      const wa = configs.find((c: any) => c.channel === 'whatsapp');
      if (wa) {
        setWaActive(wa.is_active);
        const cd = wa.config_data || {};
        if (cd.account_sid) setWaAccountSid(cd.account_sid);
        if (cd.phone_number) setWaPhoneNumber(cd.phone_number);
        if (cd.test_phone) setWaTestPhone(cd.test_phone);
      }
      const te = configs.find((c: any) => c.channel === 'teams');
      if (te) {
        setTeamsActive(te.is_active);
        const cd = te.config_data || {};
        if (cd.app_id) setTeamsAppId(cd.app_id);
      }
    } catch {}
  };

  const saveChannel = async (channel: string) => {
    setChannelsSaving(true);
    try {
      if (channel === 'whatsapp') {
        await channelsApi.upsertConfig('whatsapp', {
          is_active: waActive,
          config_data: { account_sid: waAccountSid, auth_token: waAuthToken || '***', phone_number: waPhoneNumber, test_phone: waTestPhone },
        });
      } else {
        await channelsApi.upsertConfig('teams', {
          is_active: teamsActive,
          config_data: { app_id: teamsAppId, app_secret: teamsAppSecret || '***' },
        });
      }
      showToast('Kanal gespeichert', 'success');
    } catch { showToast('Fehler beim Speichern', 'error'); }
    setChannelsSaving(false);
  };

  const testChannel = async (channel: string) => {
    try {
      await channelsApi.testConfig(channel);
      showToast('Test erfolgreich!', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Test fehlgeschlagen', 'error');
    }
  };

  const loadIntegrations = async () => {
    try {
      const res = await integrationsApi.list();
      setIntegrations(res.data);
      const outlook = res.data.find((i: any) => i.integration_type === 'outlook_calendar');
      if (outlook) {
        setOutlookStatus({ connected: outlook.is_active, account_email: outlook.account_email, last_sync_at: outlook.last_sync_at, integration_id: outlook.id });
      } else {
        setOutlookStatus({ connected: false });
      }
    } catch {}
    try { const res = await integrationsApi.getIcalFeed(); const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/api\/v1$/, ''); setIcalUrl((apiBase || window.location.origin) + res.data.feed_url); } catch {}
    try { const res = await integrationsApi.zoomStatus(); setZoomStatus(res.data); } catch {}
    try {
      const res = await integrationsApi.googleStatus();
      setGoogleStatus(res.data);
      if (res.data.connected) {
        try { const fRes = await integrationsApi.googleDriveFolders(); setDriveFolders(fRes.data.folders || []); } catch {}
      }
    } catch {}
  };

  return (
    <div>
      <div className="mb-24">
        <h1>Integrationen</h1>
        <p className="text-muted text-sm">Verbinde externe Dienste mit CoachDesk365</p>
      </div>

      <div className="category-chips mb-24">
        <button className={`chip ${activeTab === 'email' ? 'chip-active' : ''}`} onClick={() => setActiveTab('email')}>
          <Mail size={14} style={{ marginRight: 4 }} /> E-Mail & SMTP
        </button>
        <button className={`chip ${activeTab === 'kalender' ? 'chip-active' : ''}`} onClick={() => setActiveTab('kalender')}>
          <Calendar size={14} style={{ marginRight: 4 }} /> Kalender
        </button>
        <button className={`chip ${activeTab === 'video' ? 'chip-active' : ''}`} onClick={() => setActiveTab('video')}>
          <Video size={14} style={{ marginRight: 4 }} /> Video
        </button>
        <button className={`chip ${activeTab === 'cloud' ? 'chip-active' : ''}`} onClick={() => setActiveTab('cloud')}>
          <Cloud size={14} style={{ marginRight: 4 }} /> Cloud-Speicher
        </button>
        <button className={`chip ${activeTab === 'messaging' ? 'chip-active' : ''}`} onClick={() => setActiveTab('messaging')}>
          <MessageSquare size={14} style={{ marginRight: 4 }} /> Messaging
        </button>
        <button className={`chip ${activeTab === 'webhooks' ? 'chip-active' : ''}`} onClick={() => setActiveTab('webhooks')}>
          <Zap size={14} style={{ marginRight: 4 }} /> Webhooks
        </button>
      </div>

      {activeTab === 'email' && <EmailTemplateEditor />}
      {activeTab === 'messaging' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* SMS & WhatsApp (Twilio) */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Smartphone size={20} style={{ color: '#25D366' }} />
              <h3 style={{ margin: 0 }}>SMS & WhatsApp (Twilio)</h3>
              <span style={{
                marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 600,
                padding: '2px 10px', borderRadius: 12,
                background: waActive ? 'color-mix(in srgb, #059669 12%, var(--bg-card))' : 'var(--bg-hover)',
                color: waActive ? '#059669' : 'var(--text-muted)',
              }}>{waActive ? 'Aktiv' : 'Inaktiv'}</span>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>
                Senden und empfangen Sie SMS-Nachrichten ueber Twilio.
                Erstellen Sie ein kostenloses Konto auf <a href="https://www.twilio.com" target="_blank" rel="noreferrer">twilio.com</a> und
                holen Sie sich Account SID, Auth Token und eine Telefonnummer.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Account SID</label>
                  <input className="form-input" value={waAccountSid} onChange={e => setWaAccountSid(e.target.value)} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                </div>
                <div>
                  <label className="form-label">Auth Token</label>
                  <input className="form-input" type="password" value={waAuthToken} onChange={e => setWaAuthToken(e.target.value)} placeholder="Auth Token" />
                </div>
                <div>
                  <label className="form-label">Twilio-Telefonnummer</label>
                  <input className="form-input" value={waPhoneNumber} onChange={e => setWaPhoneNumber(e.target.value)} placeholder="+49..." />
                </div>
                <div>
                  <label className="form-label">Testnummer (Ihr Handy)</label>
                  <input className="form-input" value={waTestPhone} onChange={e => setWaTestPhone(e.target.value)} placeholder="+49..." />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={waActive} onChange={e => setWaActive(e.target.checked)} />
                  Aktivieren
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={() => saveChannel('whatsapp')} disabled={channelsSaving}>
                  Speichern
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => testChannel('whatsapp')} disabled={!waActive}>
                  Verbindung testen
                </button>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                <strong>Webhook-URL (für eingehende SMS):</strong>{' '}
                <code style={{ fontSize: '0.75rem' }}>{`${process.env.REACT_APP_API_URL || ''}/channels/webhooks/twilio`}</code>
                <br/>Tragen Sie diese URL in der Twilio Console unter Ihrer Telefonnummer → Messaging → Webhook ein.
              </div>
            </div>
          </div>

          {/* Teams */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Monitor size={20} style={{ color: '#6264A7' }} />
              <h3 style={{ margin: 0 }}>Microsoft Teams (Azure Bot)</h3>
              <span style={{
                marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 600,
                padding: '2px 10px', borderRadius: 12,
                background: teamsActive ? '#ECFDF5' : '#F3F4F6',
                color: teamsActive ? '#059669' : '#9CA3AF',
              }}>{teamsActive ? 'Aktiv' : 'Inaktiv'}</span>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>
                Empfangen und senden Sie Teams-Nachrichten ueber einen Azure Bot.
                Registrieren Sie einen Bot im Azure Portal und tragen Sie die Credentials hier ein.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">App-ID (Client ID)</label>
                  <input className="form-input" value={teamsAppId} onChange={e => setTeamsAppId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                </div>
                <div>
                  <label className="form-label">App-Secret</label>
                  <input className="form-input" type="password" value={teamsAppSecret} onChange={e => setTeamsAppSecret(e.target.value)} placeholder="Client Secret" />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={teamsActive} onChange={e => setTeamsActive(e.target.checked)} />
                  Aktivieren
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={() => saveChannel('teams')} disabled={channelsSaving}>
                  Speichern
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => testChannel('teams')} disabled={!teamsActive}>
                  Verbindung testen
                </button>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                <strong>Messaging Endpoint:</strong>{' '}
                <code style={{ fontSize: '0.75rem' }}>{`${process.env.REACT_APP_API_URL || ''}/channels/webhooks/teams`}</code>
                <br/>Tragen Sie diese URL in Azure Portal unter Bot Configuration → Messaging Endpoint ein.
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'webhooks' && <WebhooksSettings />}

      {/* Kalender Tab */}
      {activeTab === 'kalender' && (
        <div className="card" style={{ maxWidth: 720, padding: 32 }}>
          <h3><Link size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Kalender-Integrationen</h3>
          <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
            Verbinde deinen Outlook- oder Google-Kalender für bidirektionale Synchronisierung.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {integrations.filter(i => i.type !== 'ical_feed' && i.type !== 'zoom_video').map((integ) => (
              <div key={integ.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 16px', background: integ.is_active ? 'color-mix(in srgb, #059669 10%, var(--bg-card))' : 'var(--bg-hover)', borderRadius: 8,
              }}>
                <div>
                  <strong style={{ fontSize: '0.9rem' }}>{integ.name}</strong>
                  {integ.account_email && <span className="text-muted text-sm" style={{ marginLeft: 8 }}>{integ.account_email}</span>}
                  {integ.last_sync_at && <p className="text-muted text-sm" style={{ margin: 0 }}>Letzte Sync: {new Date(integ.last_sync_at).toLocaleString('de-DE')}</p>}
                </div>
                <div className="flex gap-8">
                  {integ.is_active && (
                    <button className="btn btn-sm btn-secondary" onClick={async () => {
                      try { const res = await integrationsApi.sync(integ.id); showToast(`Sync: ${res.data.pushed} gepusht, ${res.data.pulled} gelesen`, 'success'); loadIntegrations(); }
                      catch (err: any) { showToast(err.response?.data?.detail || 'Sync fehlgeschlagen', 'error'); }
                    }}><RefreshCw size={14} /> Sync</button>
                  )}
                  <button className="btn btn-sm btn-danger" onClick={async () => {
                    if (!window.confirm('Integration trennen?')) return;
                    try { await integrationsApi.disconnect(integ.id); loadIntegrations(); } catch { showToast('Trennen fehlgeschlagen', 'error'); }
                  }}><Unlink size={14} /></button>
                </div>
              </div>
            ))}
            {icalUrl && (
              <div style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 8 }}>
                <strong style={{ fontSize: '0.9rem' }}>iCal-Feed URL</strong>
                <p className="text-muted text-sm" style={{ margin: '4px 0 8px' }}>Abonniere diesen Feed in Apple Calendar, Thunderbird oder jedem anderen Kalender-Programm.</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input readOnly value={icalUrl} style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.8rem' }} />
                  <button className="btn btn-sm btn-secondary" onClick={() => { navigator.clipboard.writeText(icalUrl); showToast('URL kopiert!', 'success'); }}>
                    <Copy size={14} /> Kopieren
                  </button>
                </div>
              </div>
            )}
            {/* Google Calendar Sync Settings */}
            {googleStatus.connected && (
              <div style={{ padding: '16px', background: '#e8f5e9', borderRadius: 8, border: '1px solid var(--border)' }}>
                <strong style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <Calendar size={14} /> Google Kalender
                </strong>
                {googleStatus.account_email && <p className="text-muted text-sm" style={{ margin: '0 0 8px' }}>Konto: {googleStatus.account_email}</p>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: googleStatus.auto_sync ? '#e8f5e9' : 'var(--bg)', borderRadius: 6, cursor: 'pointer', marginBottom: 6 }}
                  onClick={async () => { try { await integrationsApi.googleUpdateSettings({ auto_sync: !googleStatus.auto_sync }); setGoogleStatus({ ...googleStatus, auto_sync: !googleStatus.auto_sync }); } catch { showToast('Fehler', 'error'); } }}>
                  <input type="checkbox" checked={googleStatus.auto_sync || false} readOnly style={{ width: 18, height: 18, cursor: 'pointer' }} />
                  <span style={{ fontSize: '0.85rem' }}>Termine automatisch synchronisieren</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: googleStatus.invite_clients ? '#e8f5e9' : 'var(--bg)', borderRadius: 6, cursor: 'pointer' }}
                  onClick={async () => { try { await integrationsApi.googleUpdateSettings({ invite_clients: !googleStatus.invite_clients }); setGoogleStatus({ ...googleStatus, invite_clients: !googleStatus.invite_clients }); } catch { showToast('Fehler', 'error'); } }}>
                  <input type="checkbox" checked={googleStatus.invite_clients || false} readOnly style={{ width: 18, height: 18, cursor: 'pointer' }} />
                  <span style={{ fontSize: '0.85rem' }}>Klienten als Gäste einladen</span>
                </div>
                {googleStatus.last_sync_at && <p className="text-muted text-sm" style={{ margin: '8px 0 0', fontSize: '0.75rem' }}>Letzte Sync: {new Date(googleStatus.last_sync_at).toLocaleString('de-DE')}</p>}
                {googleStatus.integration_id && (
                  <button className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={async () => {
                    try { const res = await integrationsApi.sync(googleStatus.integration_id!); showToast(`Sync: ${res.data.pushed} gepusht, ${res.data.pulled} gelesen`, 'success'); loadIntegrations(); }
                    catch (err: any) { showToast(err.response?.data?.detail || 'Sync fehlgeschlagen', 'error'); }
                  }}><RefreshCw size={14} /> Jetzt synchronisieren</button>
                )}
              </div>
            )}
            {!googleStatus.connected && (
              <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Cloud size={18} style={{ color: '#4285F4' }} />
                  <strong style={{ fontSize: '0.95rem' }}>Google verbinden</strong>
                </div>
                <p className="text-muted text-sm" style={{ margin: '0 0 12px' }}>
                  Verbinde Google, um Termine in Google Calendar zu synchronisieren und Dokumente in Google Drive zu verwalten.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Client ID</label>
                    <input value={googleClientId} onChange={(e) => setGoogleClientId(e.target.value)} placeholder="Deine Google Client ID" style={{ fontSize: '0.85rem' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Client Secret</label>
                    <input type="password" value={googleClientSecret} onChange={(e) => setGoogleClientSecret(e.target.value)} placeholder="Dein Google Client Secret" style={{ fontSize: '0.85rem' }} />
                  </div>
                </div>
                <button className="btn btn-sm btn-primary" disabled={googleConnecting || !googleClientId || !googleClientSecret}
                  onClick={async () => {
                    setGoogleConnecting(true);
                    try {
                      const redirectUri = window.location.origin + '/integrationen';
                      const res = await integrationsApi.googleConnect({ client_id: googleClientId, redirect_uri: redirectUri });
                      const authWindow = window.open(res.data.auth_url, 'google_oauth', 'width=600,height=700');
                      oauthWindowRef.current = authWindow;
                      oauthIntervalRef.current = setInterval(() => {
                        try {
                          if (authWindow?.closed) { clearInterval(oauthIntervalRef.current!); setGoogleConnecting(false); return; }
                          const url = authWindow?.location?.href;
                          if (url && url.startsWith(window.location.origin)) {
                            clearInterval(oauthIntervalRef.current!); authWindow?.close();
                            const code = new URLSearchParams(new URL(url).search).get('code');
                            if (code) {
                              integrationsApi.googleCallback({ code, client_id: googleClientId, client_secret: googleClientSecret, redirect_uri: redirectUri })
                                .then(() => { setGoogleClientId(''); setGoogleClientSecret(''); loadIntegrations(); })
                                .catch((err: any) => showToast(err.response?.data?.detail || 'Google-Verbindung fehlgeschlagen', 'error'))
                                .finally(() => setGoogleConnecting(false));
                            } else { setGoogleConnecting(false); }
                          }
                        } catch {}
                      }, 500);
                    } catch (err: any) { showToast(err.response?.data?.detail || 'Fehler', 'error'); setGoogleConnecting(false); }
                  }}>
                  <Cloud size={14} /> {googleConnecting ? 'Verbinde...' : 'Mit Google verbinden'}
                </button>
              </div>
            )}
            {/* Outlook Calendar */}
            {outlookStatus.connected ? (
              <div style={{ padding: '16px', background: '#e8f5e9', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Calendar size={18} style={{ color: '#0078D4' }} />
                  <strong style={{ fontSize: '0.95rem' }}>Microsoft Outlook</strong>
                  <span style={{ fontSize: '0.75rem', color: '#2e7d32', background: '#c8e6c9', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>Verbunden</span>
                </div>
                {outlookStatus.account_email && <p className="text-muted text-sm" style={{ margin: '0 0 8px' }}>Konto: {outlookStatus.account_email}</p>}
                {outlookStatus.last_sync_at && <p className="text-muted text-sm" style={{ margin: '0 0 8px', fontSize: '0.75rem' }}>Letzte Sync: {new Date(outlookStatus.last_sync_at).toLocaleString('de-DE')}</p>}
                <div className="flex gap-8">
                  <button className="btn btn-sm btn-secondary" onClick={async () => {
                    try { const res = await integrationsApi.sync(outlookStatus.integration_id!); showToast(`Sync: ${res.data.pushed} gepusht, ${res.data.pulled} gelesen`, 'success'); loadIntegrations(); }
                    catch (err: any) { showToast(err.response?.data?.detail || 'Sync fehlgeschlagen', 'error'); }
                  }}><RefreshCw size={14} /> Jetzt synchronisieren</button>
                  <button className="btn btn-sm btn-danger" onClick={async () => {
                    if (!window.confirm('Outlook-Verbindung trennen?')) return;
                    try { await integrationsApi.disconnect(outlookStatus.integration_id!); setOutlookStatus({ connected: false }); loadIntegrations(); }
                    catch { showToast('Trennen fehlgeschlagen', 'error'); }
                  }}><Unlink size={14} /> Trennen</button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '16px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Calendar size={18} style={{ color: '#0078D4' }} />
                  <strong style={{ fontSize: '0.95rem' }}>Outlook verbinden</strong>
                </div>
                <p className="text-muted text-sm" style={{ margin: '0 0 12px' }}>
                  Verbinde Microsoft Outlook, um Termine zu synchronisieren.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Client ID</label>
                    <input value={outlookClientId} onChange={(e) => setOutlookClientId(e.target.value)} placeholder="Azure AD Client ID" style={{ fontSize: '0.85rem' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem' }}>Client Secret</label>
                    <input type="password" value={outlookClientSecret} onChange={(e) => setOutlookClientSecret(e.target.value)} placeholder="Azure AD Client Secret" style={{ fontSize: '0.85rem' }} />
                  </div>
                </div>
                <button className="btn btn-sm btn-primary" disabled={outlookConnecting || !outlookClientId || !outlookClientSecret}
                  onClick={async () => {
                    setOutlookConnecting(true);
                    try {
                      const redirectUri = window.location.origin + '/integrationen';
                      const res = await integrationsApi.outlookConnect({ client_id: outlookClientId, redirect_uri: redirectUri });
                      const authWindow = window.open(res.data.auth_url, 'outlook_oauth', 'width=600,height=700');
                      oauthWindowRef.current = authWindow;
                      oauthIntervalRef.current = setInterval(() => {
                        try {
                          if (authWindow?.closed) { clearInterval(oauthIntervalRef.current!); setOutlookConnecting(false); return; }
                          const url = authWindow?.location?.href;
                          if (url && url.startsWith(window.location.origin)) {
                            clearInterval(oauthIntervalRef.current!); authWindow?.close();
                            const code = new URLSearchParams(new URL(url).search).get('code');
                            if (code) {
                              integrationsApi.outlookCallback({ code, client_id: outlookClientId, client_secret: outlookClientSecret, redirect_uri: redirectUri })
                                .then(() => { setOutlookClientId(''); setOutlookClientSecret(''); loadIntegrations(); })
                                .catch((err: any) => showToast(err.response?.data?.detail || 'Outlook-Verbindung fehlgeschlagen', 'error'))
                                .finally(() => setOutlookConnecting(false));
                            } else { setOutlookConnecting(false); }
                          }
                        } catch {}
                      }, 500);
                    } catch (err: any) { showToast(err.response?.data?.detail || 'Fehler', 'error'); setOutlookConnecting(false); }
                  }}>
                  <Calendar size={14} /> {outlookConnecting ? 'Verbinde...' : 'Mit Outlook verbinden'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Video Tab */}
      {activeTab === 'video' && (
        <div className="card" style={{ maxWidth: 720, padding: 32 }}>
          <div style={{ padding: '16px', background: zoomStatus.connected ? '#e8f5e9' : '#f8f5f0', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Video size={18} style={{ color: '#2D8CFF' }} />
              <strong style={{ fontSize: '0.95rem' }}>Zoom</strong>
              {zoomStatus.connected && <span style={{ fontSize: '0.75rem', color: '#2e7d32', background: '#c8e6c9', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>Verbunden</span>}
            </div>
            {zoomStatus.connected ? (
              <div>
                {zoomStatus.account_email && <p className="text-muted text-sm" style={{ margin: '0 0 8px' }}>Konto: {zoomStatus.account_email}</p>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: zoomStatus.auto_create_meetings ? '#e8f5e9' : '#f8f5f0', borderRadius: 6, marginTop: 8, cursor: 'pointer' }}
                  onClick={async () => { try { await integrationsApi.zoomUpdateSettings({ auto_create_meetings: !zoomStatus.auto_create_meetings }); setZoomStatus({ ...zoomStatus, auto_create_meetings: !zoomStatus.auto_create_meetings }); } catch { showToast('Fehler', 'error'); } }}>
                  <input type="checkbox" checked={zoomStatus.auto_create_meetings || false} readOnly style={{ width: 18, height: 18, cursor: 'pointer' }} />
                  <span style={{ fontSize: '0.85rem' }}>Automatisch Zoom-Meeting erstellen bei Online-Sitzungen</span>
                </div>
                <button className="btn btn-sm btn-danger" style={{ marginTop: 8 }} onClick={async () => {
                  if (!window.confirm('Zoom-Verbindung trennen?')) return;
                  try { if (zoomStatus.integration_id) await integrationsApi.disconnect(zoomStatus.integration_id); setZoomStatus({ connected: false }); loadIntegrations(); }
                  catch { showToast('Trennen fehlgeschlagen', 'error'); }
                }}><Unlink size={14} /> Trennen</button>
              </div>
            ) : (
              <div>
                <p className="text-muted text-sm" style={{ margin: '0 0 12px' }}>Verbinde Zoom, um automatisch Meeting-Links für Online-Sitzungen zu erstellen.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: '0.8rem' }}>Account ID</label><input value={zoomAccountId} onChange={(e) => setZoomAccountId(e.target.value)} placeholder="Deine Zoom Account ID" style={{ fontSize: '0.85rem' }} /></div>
                  <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: '0.8rem' }}>Client ID</label><input value={zoomClientId} onChange={(e) => setZoomClientId(e.target.value)} placeholder="Deine Zoom Client ID" style={{ fontSize: '0.85rem' }} /></div>
                  <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: '0.8rem' }}>Client Secret</label><input type="password" value={zoomClientSecret} onChange={(e) => setZoomClientSecret(e.target.value)} placeholder="Dein Zoom Client Secret" style={{ fontSize: '0.85rem' }} /></div>
                </div>
                <button className="btn btn-sm btn-primary" disabled={zoomConnecting || !zoomAccountId || !zoomClientId || !zoomClientSecret}
                  onClick={async () => {
                    setZoomConnecting(true);
                    try {
                      await integrationsApi.zoomConnect({ account_id: zoomAccountId, client_id: zoomClientId, client_secret: zoomClientSecret });
                      const status = await integrationsApi.zoomStatus(); setZoomStatus(status.data);
                      setZoomAccountId(''); setZoomClientId(''); setZoomClientSecret(''); loadIntegrations();
                    } catch (err: any) { showToast(err.response?.data?.detail || 'Zoom-Verbindung fehlgeschlagen', 'error'); }
                    finally { setZoomConnecting(false); }
                  }}>
                  <Video size={14} /> {zoomConnecting ? 'Verbinde...' : 'Mit Zoom verbinden'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cloud-Speicher Tab */}
      {activeTab === 'cloud' && (
        <div className="card" style={{ maxWidth: 720, padding: 32 }}>
          <h3><HardDrive size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />Google Drive</h3>
          {googleStatus.connected ? (
            <div>
              {googleStatus.account_email && <p className="text-muted text-sm" style={{ margin: '0 0 12px' }}>Konto: {googleStatus.account_email}</p>}
              <strong style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <HardDrive size={14} /> Automatischer Upload
              </strong>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 8px' }}>
                Aktiviere die Kategorien, die automatisch in Google Drive gespeichert werden sollen.
              </p>
              {([
                { key: 'auto_upload_invoices' as const, label: 'Rechnungen automatisch speichern', folder: 'Rechnungen' },
                { key: 'auto_upload_offers' as const, label: 'Angebote automatisch speichern', folder: 'Angebote' },
                { key: 'auto_upload_contracts' as const, label: 'Verträge automatisch speichern', folder: 'Verträge' },
                { key: 'auto_upload_documents' as const, label: 'Dokumente automatisch speichern', folder: 'Dokumente' },
                { key: 'auto_upload_session_notes' as const, label: 'Sitzungsnotizen automatisch speichern', folder: 'Sitzungsnotizen' },
              ] as const).map(({ key, label, folder }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: googleStatus[key] ? '#e8f5e9' : 'var(--bg)', borderRadius: 6, cursor: 'pointer', marginBottom: 4 }}
                  onClick={async () => { try { await integrationsApi.googleUpdateSettings({ [key]: !googleStatus[key] }); setGoogleStatus({ ...googleStatus, [key]: !googleStatus[key] }); } catch { showToast('Fehler', 'error'); } }}>
                  <input type="checkbox" checked={googleStatus[key] || false} readOnly style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: '0.83rem', flex: 1 }}>{label}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{folder}/</span>
                </div>
              ))}

              {(googleStatus.auto_upload_invoices || googleStatus.auto_upload_offers || googleStatus.auto_upload_contracts || googleStatus.auto_upload_documents || googleStatus.auto_upload_session_notes) && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Stammordner (optional)</label>
                  {googleStatus.drive_target_folder_name ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px', padding: '6px 10px', background: '#e8f5e9', borderRadius: 6, fontSize: '0.85rem' }}>
                      <HardDrive size={14} /><strong>{googleStatus.drive_target_folder_name}</strong>
                      <button className="btn btn-sm" style={{ marginLeft: 'auto', fontSize: '0.75rem', padding: '2px 8px' }}
                        onClick={async () => { try { await integrationsApi.googleUpdateSettings({ drive_target_folder_id: '', drive_target_folder_name: '' }); setGoogleStatus({ ...googleStatus, drive_target_folder_id: undefined, drive_target_folder_name: undefined }); } catch {} }}>
                        Ändern
                      </button>
                    </div>
                  ) : (
                    <div style={{ margin: '4px 0' }}>
                      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                        <input id="folderSearch" type="text" placeholder="Ordnername eingeben..." style={{ fontSize: '0.85rem', flex: 1 }}
                          onKeyDown={(e) => { if (e.key === 'Enter') (document.getElementById('folderSearchBtn') as any)?.click(); }} />
                        <button id="folderSearchBtn" className="btn btn-sm btn-primary" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                          onClick={async () => { const q = (document.getElementById('folderSearch') as HTMLInputElement)?.value?.trim() || undefined; try { const res = await integrationsApi.googleDriveFolders(q); setDriveFolders(res.data?.folders || []); } catch { showToast('Ordner konnten nicht geladen werden', 'error'); } }}>
                          Suchen
                        </button>
                      </div>
                      <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                        <div style={{ padding: '8px 10px', fontSize: '0.85rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', background: !googleStatus.drive_target_folder_id ? 'color-mix(in srgb, #059669 10%, var(--bg-card))' : 'var(--bg-card)' }}
                          onClick={async () => { try { await integrationsApi.googleUpdateSettings({ drive_target_folder_id: '', drive_target_folder_name: '' }); setGoogleStatus({ ...googleStatus, drive_target_folder_id: undefined, drive_target_folder_name: undefined }); } catch {} }}>
                          <HardDrive size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Stammverzeichnis (My Drive)
                        </div>
                        {driveFolders.map((f: any) => (
                          <div key={f.id} style={{ padding: '8px 10px', fontSize: '0.85rem', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                            onClick={async () => { try { await integrationsApi.googleUpdateSettings({ drive_target_folder_id: f.id, drive_target_folder_name: f.name }); setGoogleStatus({ ...googleStatus, drive_target_folder_id: f.id, drive_target_folder_name: f.name }); } catch {} }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')} onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--bg-card)')}>
                            <Folder size={13} style={{ marginRight: 6, verticalAlign: -2 }} />{f.name}
                          </div>
                        ))}
                        {driveFolders.length === 0 && <div style={{ padding: '12px 10px', fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>Klicke "Suchen" um Ordner zu laden</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button className="btn btn-sm btn-danger" style={{ marginTop: 12 }} onClick={async () => {
                if (!window.confirm('Google-Verbindung trennen?')) return;
                try { if (googleStatus.integration_id) await integrationsApi.disconnect(googleStatus.integration_id); setGoogleStatus({ connected: false }); loadIntegrations(); }
                catch { showToast('Trennen fehlgeschlagen', 'error'); }
              }}><Unlink size={14} /> Trennen</button>
            </div>
          ) : (
            <p className="text-muted text-sm">Google ist nicht verbunden. Verbinde Google unter dem Tab "Kalender".</p>
          )}
        </div>
      )}
    </div>
  );
}
